/**
 * «1 шт / Пара» — поведінка.
 * Пара до snippets/pair-price.liquid і assets/pair-price.css.
 *
 * Блок нічого не продає сам: він ставить кількість у формі товару, а в
 * кошик кладе штатна кнопка теми. «Пара» = 2, «1 шт» = 1; кількість,
 * набрана кнопками +/−, повертається в блок (3 і більше — жодна кнопка
 * не обрана, сума рахується за набрану кількість).
 *
 * Форму шукаємо за номером варіанта, а не першу в секції: у секції товару
 * є й чужі форми — «Разом купують», розстрочка Shop Pay.
 */
(() => {
  if (customElements.get('pair-price')) return;

  const reduceMotion = () => window.matchMedia('(prefers-reduced-motion: reduce)').matches;

  const formatMoney = (cents) => {
    const theme = window.theme || {};
    if (typeof theme.formatMoney === 'function') {
      return theme.formatMoney(cents, theme.moneyFormat || theme.moneyWithoutCurrencyFormat);
    }
    return `${Math.round(cents / 100).toLocaleString('uk-UA')} ₴`;
  };

  class PairPrice extends HTMLElement {
    connectedCallback() {
      if (this.ready) return;
      this.ready = true;

      try {
        this.variants = JSON.parse(this.querySelector('[data-fpp-variants]').textContent);
      } catch (error) {
        this.variants = {};
      }
      this.section = this.closest('[data-section-type]') || this.closest('.shopify-section') || document;
      this.options = Array.from(this.querySelectorAll('[data-fpp-qty]'));
      this.unitEl = this.querySelector('[data-fpp-unit]');
      this.pairEl = this.querySelector('[data-fpp-pair]');
      this.labelEl = this.querySelector('[data-fpp-label]');
      this.totalEl = this.querySelector('[data-fpp-total]');
      this.noteEl = this.querySelector('[data-fpp-note]');
      this.strikeEl = this.querySelector('[data-fpp-strike]');
      this.shown = null;

      this.onOption = this.onOption.bind(this);
      this.onQuantity = this.onQuantity.bind(this);
      this.onVariantChange = this.onVariantChange.bind(this);

      this.options.forEach((button) => button.addEventListener('click', this.onOption));
      this.addEventListener('keydown', (event) => this.onKey(event));
      this.section.addEventListener('theme:variant:change', this.onVariantChange);
      // Кнопки +/− теми міняють число в полі не завжди з подією — дивимось
      // і на клік по них, і на введення руками.
      this.section.addEventListener('input', this.onQuantity);
      this.section.addEventListener('change', this.onQuantity);
      this.onQuantityClick = (event) => {
        if (event.target.closest('.quantity__minus, .quantity__plus, [data-popout-option]')) {
          setTimeout(this.onQuantity, 0);
        }
      };
      this.section.addEventListener('click', this.onQuantityClick);

      this.variantId = this.currentVariantId();
      this.setQty(2, { silent: true });
      this.reveal();
    }

    disconnectedCallback() {
      if (this.section) {
        this.section.removeEventListener('theme:variant:change', this.onVariantChange);
        this.section.removeEventListener('input', this.onQuantity);
        this.section.removeEventListener('change', this.onQuantity);
        this.section.removeEventListener('click', this.onQuantityClick);
      }
      if (this.observer) this.observer.disconnect();
    }

    // ---------------------------------------------------------------
    // Форма товару
    // ---------------------------------------------------------------

    // Поля форми беремо через `form.elements`: у Broadcast номер варіанта
    // й кількість бувають поза <form>, привʼязані атрибутом form=, і
    // querySelector усередині форми їх не бачить. І `form.id` тут читати не
    // можна — його перекриває поле з іменем id.
    productForms() {
      return Array.from(this.section.querySelectorAll('form[action*="/cart/add"]')).filter((form) => {
        if (form.classList.contains('installment')) return false;
        const id = form.elements.namedItem('id');
        if (id && id.value) return Object.prototype.hasOwnProperty.call(this.variants, String(id.value));
        return form.classList.contains('product-form');
      });
    }

    quantityInputs() {
      const inputs = [];
      this.productForms().forEach((form) => {
        Array.from(form.elements).forEach((field) => {
          if (field.name === 'quantity' && field.tagName === 'INPUT') inputs.push(field);
        });
      });
      return inputs;
    }

    currentVariantId() {
      const form = this.productForms()[0];
      const id = form && form.elements.namedItem('id');
      const value = id && id.value ? String(id.value) : '';
      return Object.prototype.hasOwnProperty.call(this.variants, value) ? value : Object.keys(this.variants)[0];
    }

    // ---------------------------------------------------------------
    // Події
    // ---------------------------------------------------------------

    onOption(event) {
      this.setQty(Number(event.currentTarget.dataset.fppQty));
    }

    onKey(event) {
      if (!['ArrowLeft', 'ArrowRight', 'ArrowUp', 'ArrowDown'].includes(event.key)) return;
      if (!event.target.closest('[data-fpp-qty]')) return;
      event.preventDefault();
      const next = this.qty === 2 ? 1 : 2;
      this.setQty(next);
      this.options.find((button) => Number(button.dataset.fppQty) === next).focus();
    }

    onQuantity(event) {
      if (event && event.target && event.target.name !== 'quantity') return;
      const input = this.quantityInputs()[0];
      if (!input) return;
      const qty = Math.max(1, parseInt(input.value, 10) || 1);
      if (qty !== this.qty) this.setQty(qty, { fromForm: true });
    }

    onVariantChange(event) {
      const variant = event.detail && event.detail.variant;
      if (!variant) return;
      this.variantId = String(variant.id);
      this.variants[this.variantId] = [variant.price, variant.compare_at_price || 0];
      this.render(true);
    }

    // ---------------------------------------------------------------
    // Стан
    // ---------------------------------------------------------------

    setQty(qty, { silent = false, fromForm = false } = {}) {
      this.qty = qty;
      if (!fromForm) {
        this.quantityInputs().forEach((input) => {
          if (String(input.value) === String(qty)) return;
          input.value = qty;
          input.dispatchEvent(new Event('input', { bubbles: true }));
          input.dispatchEvent(new Event('change', { bubbles: true }));
          this.syncDropdown(input, qty);
        });
      }
      this.dataset.qty = qty === 1 || qty === 2 ? String(qty) : 'other';
      this.options.forEach((button) => {
        const on = Number(button.dataset.fppQty) === qty;
        button.setAttribute('aria-checked', on ? 'true' : 'false');
        button.tabIndex = on || (qty > 2 && button.dataset.fppQty === '2') ? 0 : -1;
      });
      this.render(!silent);
    }

    // Кількість у темі — випадний список (quantity_style = dropdown):
    // поле приховане, а на кнопці — свій напис. Число в полі його не міняє.
    syncDropdown(input, qty) {
      const counter = input.closest('quantity-counter');
      if (!counter) return;
      const label = counter.querySelector('[data-popout-toggle-text]');
      if (label) label.textContent = qty;
      counter.querySelectorAll('[data-popout-option]').forEach((option) => {
        const on = Number(option.dataset.value) === qty;
        option.closest('li')?.classList.toggle('is-active', on);
        if (on) option.setAttribute('aria-current', 'true');
        else option.removeAttribute('aria-current');
      });
    }

    render(animate) {
      const [price, compare] = this.variants[this.variantId] || [0, 0];
      const qty = this.qty || 1;
      this.unitEl.textContent = formatMoney(price);
      this.pairEl.textContent = formatMoney(price * 2);

      if (qty === 2) this.labelEl.textContent = 'Разом за пару';
      else if (qty === 1) this.labelEl.textContent = 'За 1 шт';
      else this.labelEl.textContent = `Разом за ${qty} шт`;

      this.noteEl.textContent = qty === 1 ? 'ціна за штуку' : `${qty} × ${formatMoney(price)}`;
      this.countTo(price * qty, animate);

      if (compare > price) {
        this.strikeEl.textContent = formatMoney(compare * qty);
        this.strikeEl.hidden = false;
      } else {
        this.strikeEl.textContent = '';
        this.strikeEl.hidden = true;
      }

      if (animate && !reduceMotion()) {
        this.classList.remove('is-sheen');
        // Перезапуск анімації відблиску: знімаємо клас і чекаємо кадр.
        requestAnimationFrame(() => requestAnimationFrame(() => this.classList.add('is-sheen')));
      }
    }

    countTo(target, animate) {
      cancelAnimationFrame(this.frame);
      const from = this.shown;
      this.shown = target;
      if (!animate || from === null || from === target || reduceMotion()) {
        this.totalEl.textContent = formatMoney(target);
        return;
      }
      const start = performance.now();
      const duration = 650;
      const step = (now) => {
        const t = Math.min(1, (now - start) / duration);
        const eased = 1 - Math.pow(1 - t, 3);
        // Цілі гривні в дорозі, точна сума — в кінці.
        const value = Math.round((from + (target - from) * eased) / 100) * 100;
        this.totalEl.textContent = formatMoney(t < 1 ? value : target);
        if (t < 1) this.frame = requestAnimationFrame(step);
      };
      this.frame = requestAnimationFrame(step);
    }

    reveal() {
      if (reduceMotion() || !('IntersectionObserver' in window)) {
        this.classList.add('is-in');
        return;
      }
      this.observer = new IntersectionObserver((entries) => {
        if (entries.some((entry) => entry.isIntersecting)) {
          this.classList.add('is-in');
          this.observer.disconnect();
        }
      }, { threshold: 0.35 });
      this.observer.observe(this);
    }
  }

  customElements.define('pair-price', PairPrice);
})();
